import { isDefined } from '@aloft/toolkit-shared';
import { Block, Button, Flex, Typography, getSortIcon } from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-sui-chakra
import { ChakraProps, chakra } from '@aloft/toolkit-sui/chakra';
import { Row as ReactTableRow, Table, flexRender } from '@tanstack/react-table';

import {
  Fragment,
  ReactNode,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { MemoizedRow } from './components/MemoizedRow.tsx';
import { Row } from './components/Row.tsx';
import { HEADER_HEIGHT_IN_PX, PADDING_X_IN_PX, sharedCellStyles } from './constants.ts';
import { getCellWidthStyles } from './getCellWidthStyles.ts';
import { TPTableClickRow } from './types.ts';
import { usePinnedColumnStyles } from './usePinnedColumnStyles.ts';
import { useVirtualizedRows } from './useVirtualizedRows.ts';
import { makeRowID } from './utils/makeRowID.ts';

const GROUP_ROW_ENTER_MS = 200;

export type TableRowGrouping<T> = {
  getGroupId: (row: T) => string | undefined;
  groups: {
    id: string;
    count: number;
    collapsed: boolean;
    renderHeader: (colSpan: number) => ReactNode;
  }[];
};

type Props<T> = {
  table: Table<T>;
  shouldPinFirstRow: boolean | number;
  activeRowIndex?: number | null;
  emptyTableMessage?: string;
  onRowClick?: TPTableClickRow<T>;
  __dangerOverrideInternalSelectionState?: boolean;
  memoizeRows?: boolean;
  getRowBackgroundColor?: (row: ReactTableRow<T>) => string | undefined;
  useVirtualization?: boolean;
  rowGrouping?: TableRowGrouping<T>;
};

export const ToolkitPropertyTableUI = <T,>({
  table,
  shouldPinFirstRow,
  activeRowIndex = null,
  emptyTableMessage = 'No properties found',
  onRowClick,
  __dangerOverrideInternalSelectionState = false,
  memoizeRows = false,
  getRowBackgroundColor,
  useVirtualization = true,
  rowGrouping,
}: Props<T>) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const virtualizationEnabled = useVirtualization && !rowGrouping;

  const numPinnedColumns = table.getState().columnPinning.left?.length ?? 0;
  const pinnedColumnStyles = usePinnedColumnStyles({
    paddingX: PADDING_X_IN_PX,
    numColumns: numPinnedColumns,
  });

  const { rows: reactTableRows } = table.getRowModel();
  const pinnedCount = Number(shouldPinFirstRow);
  const groupCollapsedKey = rowGrouping?.groups.map((g) => `${g.id}:${g.collapsed}`).join('|');
  const prevGroupCollapsedRef = useRef<Record<string, boolean>>({});
  const [enteringGroupIds, setEnteringGroupIds] = useState<ReadonlySet<string>>(() => new Set());

  useLayoutEffect(() => {
    if (!rowGrouping) {
      prevGroupCollapsedRef.current = {};
      return;
    }

    const newlyEntering = new Set<string>();
    rowGrouping.groups.forEach((group) => {
      const wasCollapsed = prevGroupCollapsedRef.current[group.id];
      if (wasCollapsed === true && !group.collapsed) {
        newlyEntering.add(group.id);
      }
      prevGroupCollapsedRef.current[group.id] = group.collapsed;
    });

    if (newlyEntering.size === 0) return;

    setEnteringGroupIds(newlyEntering);
    const timeoutId = window.setTimeout(() => {
      setEnteringGroupIds(new Set());
    }, GROUP_ROW_ENTER_MS);
    return () => window.clearTimeout(timeoutId);
  }, [groupCollapsedKey, rowGrouping]);

  const { virtualRows, virtualPaddingTop, virtualPaddingBottom } = useVirtualizedRows({
    numRows: reactTableRows.length,
    shouldPinFirstRow,
    containerRef,
    overscan: 7,
    enabled: virtualizationEnabled,
  });

  const handleRowClick = useCallback(
    (row: ReactTableRow<T>) => {
      onRowClick?.(row.original);
    },
    [onRowClick],
  );

  const rows = memoizeRows || !virtualizationEnabled ? reactTableRows : virtualRows;
  const pinnedRows = virtualizationEnabled ? reactTableRows.slice(0, pinnedCount) : [];
  const totalPages = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex + 1;
  const colSpan = table.getVisibleLeafColumns().length;

  const groupedBody = useMemo(() => {
    if (!rowGrouping) return null;

    const pinned = reactTableRows.slice(0, pinnedCount);
    const unpinned = reactTableRows.slice(pinnedCount);
    const rowsByGroup = new Map<string, ReactTableRow<T>[]>();
    const ungrouped: ReactTableRow<T>[] = [];

    rowGrouping.groups.forEach((group) => rowsByGroup.set(group.id, []));
    unpinned.forEach((row) => {
      const groupId = rowGrouping.getGroupId(row.original);
      if (groupId && rowsByGroup.has(groupId)) {
        rowsByGroup.get(groupId)!.push(row);
      } else {
        ungrouped.push(row);
      }
    });

    return { pinned, rowsByGroup, ungrouped };
  }, [pinnedCount, reactTableRows, rowGrouping]);

  const isTableEmpty = rows.length === 0;
  const RowComponent = memoizeRows ? MemoizedRow : Row;

  const renderDataRow = (row: ReactTableRow<T>, animateEnter = false) => (
    <RowComponent
      key={row.id}
      id={__dangerOverrideInternalSelectionState ? undefined : makeRowID(row.index)}
      isSelected={
        __dangerOverrideInternalSelectionState
          ? null
          : !!table.getSelectedRowModel().rowsById[row.id]
      }
      row={row}
      activeRowIndex={activeRowIndex}
      shouldPinFirstRow={shouldPinFirstRow}
      handleRowClick={handleRowClick}
      enableHoverStyle={isDefined(onRowClick)}
      pinnedColumnStyles={pinnedColumnStyles}
      customBackgroundColor={getRowBackgroundColor?.(row)}
      animateEnter={animateEnter}
    />
  );

  return (
    <Flex flexDirection="column" height="100%">
      <Block ref={containerRef} overflow="auto" flex={1}>
        <chakra.table width="100%" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
          <chakra.thead position="sticky" top="0px" zIndex="1" height={`${HEADER_HEIGHT_IN_PX}px`}>
            {table.getHeaderGroups().map((headerGroup) => (
              <chakra.tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  pinnedColumnStyles.setStyleInfo(header.column);

                  const styles: ChakraProps = {
                    ...sharedCellStyles,
                    ...pinnedColumnStyles.getStyles(header.column),
                    ...getCellWidthStyles(header.getSize()),
                    backgroundColor: 'surface2',
                    borderBottom: '2px solid',
                    borderColor: 'strokeDefault',
                    verticalAlign: 'middle',
                  };
                  const content = flexRender(header.column.columnDef.header, header.getContext());

                  return (
                    <chakra.th key={header.id} {...styles}>
                      <Flex
                        onClick={header.column.getToggleSortingHandler()}
                        cursor={canSort ? 'pointer' : undefined}
                        alignItems="center"
                        gap={1}
                      >
                        {content}
                        {canSort && getSortIcon(header.column)}
                      </Flex>
                    </chakra.th>
                  );
                })}
              </chakra.tr>
            ))}
          </chakra.thead>
          <chakra.tbody>
            {groupedBody ? (
              <>
                {groupedBody.pinned.map((row) => renderDataRow(row))}
                {rowGrouping!.groups.map((group) => (
                  <Fragment key={group.id}>
                    {group.renderHeader(colSpan)}
                    {!group.collapsed &&
                      (groupedBody.rowsByGroup.get(group.id) ?? []).map((row) =>
                        renderDataRow(row, enteringGroupIds.has(group.id)),
                      )}
                  </Fragment>
                ))}
                {groupedBody.ungrouped.map((row) => renderDataRow(row))}
              </>
            ) : (
              <>
                {pinnedRows.map((row) => renderDataRow(row))}
                {!memoizeRows && virtualPaddingTop && (
                  <tr>
                    <chakra.td height={virtualPaddingTop} />
                  </tr>
                )}
                {rows.map((x) => {
                  const row =
                    memoizeRows || !virtualizationEnabled
                      ? (x as ReactTableRow<T>)
                      : reactTableRows[x.index];
                  if (!row) return null;
                  return renderDataRow(row);
                })}
                {!memoizeRows && virtualPaddingBottom && (
                  <tr>
                    <chakra.td height={virtualPaddingBottom} />
                  </tr>
                )}
              </>
            )}
          </chakra.tbody>
        </chakra.table>
        {isTableEmpty && (
          <Flex padding={4} justifyContent="center">
            <Typography>{emptyTableMessage}</Typography>
          </Flex>
        )}
      </Block>

      {totalPages > 1 && (
        <Flex justifyContent="center" alignItems="center" gap={4} padding={4}>
          <Button
            variant="inlineSecondary"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            size="md"
            text="Previous"
          />
          <Typography>
            Page {currentPage} of {totalPages}
          </Typography>

          <Button
            variant="inlineSecondary"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            size="md"
            text="Next"
          />
        </Flex>
      )}
    </Flex>
  );
};
